<template>
  <info-panel
    v-if="incident"
    :item="incident"
    :title="incident.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>
    
    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(incident.description)">
      <formatted-read-more-text
        :value="incident.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <!-- EFFECTS -->
    <template #label-effects>
      {{ $t('objectNames.effect', 2) }}
    </template>
    <template #effects v-if="settingsValues.showEmptySections || visibleEffects.length > 0">
      <div v-if="visibleEffects.length > 0" class="line_effect">
        <template v-for="effect in visibleEffects" :key="effect.id">
          <item
            :id="effect.affectedId"
            v-slot="{ item: effectItem }"
            :type="effect.affectedType"
          >
            <button-item @click="openOverlay(effectItem)">
              <span v-if="effectItem.title">{{ effectItem.title }}</span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </button-item>
          </item>
          <effect-amount :effect="effect" :inline-expression="true" :rounds="rounds" :round-index="selectedRound?.index" />
        </template>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noX", [$t("objectNames.effect", 2)]) }}
      </p>
    </template>

    <!-- TRIGGERS -->
    <template #label-triggers>
      {{ $t('objectNames.trigger', 2) }}
    </template>
    <template #triggers>
      <trigger-list :incident-id="incident.id" />
    </template>

    <!-- SCENARIOS -->
    <template #label-scenarios>
      {{ $t("objectNames.scenario", 2) }}
    </template>
    <template #scenarios v-if="settingsValues.showEmptySections || scenarios.length > 0">
      <p class="mt-0">
        {{ $t("texts.scenariosIncludingIncident") }}
      </p>
      <button-list :items="scenarios" prop="title" @select="openOverlay" />
    </template>

    <template #label-dependent-incidents>
      {{ $t('objectNames.dependentIncident', 2) }}
    </template>
    <template #dependent-incidents v-if="settingsValues.showEmptySections || dependingIncidents.length > 0">
      <depending-incidents-list :activator="incident" @select="openOverlay" />
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import Item from "@editor/items/StoreItem.vue";
import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import TriggerList from "@editor/items/incidentTrigger/TriggerList.vue";
import DependingIncidentsList from "../incident/DependingIncidentsList.vue";

import { useEffectStore } from "../effect/effects.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useScenarioStore } from "../scenario/scenarios.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import { getDependingIncidents } from "./incidentFunctions";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { IncidentItem } from "./IncidentItem";
import type { EffectItem } from "../effect/EffectItem";


const props = defineProps<{
  incident: IncidentItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const route = useRoute();
const imageStore = useImageStore();
const simulationViewStore = useSimulationViewStore();
const effectStore = useEffectStore();

const sections = reactive({
  description: true,
  effects: true,
  triggers: true,
  "dependent-incidents": true,
  scenarios: false
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true },
  image: {
    type: "dropdown" as const,
    value: "side-cut",
    options: ["side-full", "side-cut", "hidden"]
  },
});

const infoPanelStore = useInfoPanelStore();

const dependingIncidents = computed(() => {
  if (!props.incident) return [];
  return getDependingIncidents(props.incident);
});

const visibleEffects = computed(() => {
  if (!props.incident) return [];
  const effects = effectStore.getByActivator(props.incident) || [];
  if (scenario.value) {
    if (scenario.value.effectsVisibility == "hidden") return [];
    if (scenario.value.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
    if (scenario.value.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  }
  if (props.incident?.effectsVisibility == "hidden") return [];
  if (props.incident?.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
  if (props.incident?.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  return effects.filter((effect) => effect.visibility !== "hidden");
});

const scenarios = computed(() => {
  if (props.incident == null) return [];
  return useScenarioStore().getByIncident(props.incident.id);
});

const scenario = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return null;
  return simulationViewStore.scenario;
});

const rounds = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return undefined;
  return simulationViewStore.rounds;
});
const selectedRound = computed(() => {
  if (!["simulation", "inspector", "evaluation"].includes(route?.meta?.areaName as string)) return null;
  return simulationViewStore.selectedRound;
});

// IMAGE

const image = computed(() => {
  if (props.incident && props.incident.imageId != null) {
    return imageStore.items[props.incident.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});


function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}


function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.event = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.event || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.event = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.event || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style></style>
